import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Level = 'poslovnica' | 'regija' | 'maloprodaja' | 'drzava';
type Unit = 'spot' | 'fine';
type Metal = 'AU' | 'AG';

type Sums = { qty: number; imam_ju_qty: number; free_qty: number; fine_g: number; spot_eur: number; book_eur: number };

type Scopes = { warehouses: { code: string; name: string; region: string | null; country: string }[]; regions: string[]; countries: string[] };
type Spot = { gold_per_gram: number; silver_per_gram: number; spot_date: string | null; silver_approx: boolean };

type StockArticle = Sums & { sku: string; item_name: string; metal: Metal; by_warehouse: (Sums & { warehouse_code: string; warehouse_name: string })[] };
type StockResp = { stock_as_of: string | null; spot: Spot; totals: Record<Metal, Sums>; articles: StockArticle[] };

type OrderRow = {
    order_number: string; warehouse_code: string; partner_name: string; branch_name: string; date: string | null;
    total_eur: number; imam_ju: boolean; au_fine_g: number; ag_fine_g: number; au_eur: number; ag_eur: number; spot_eur: number;
};
type OrderArticle = Sums & { item_name: string; metal: Metal; order_count: number; order_numbers: string[] };
type SizeBucket = { key: string; orders: number; total_eur: number; au_fine_g: number; ag_fine_g: number; spot_eur: number; order_numbers: string[] };
type DayRow = { date: string | null; orders: number; au_eur: number; ag_eur: number; au_fine_g: number; ag_fine_g: number; order_numbers: string[] };
type OrdersResp = {
    open_batch: string | null; spot: Spot;
    totals: { orders: number; total_eur: number; items_eur?: number; packaging_eur?: number; header_minus_items_eur?: number } & Record<Metal, Sums>;
    articles: OrderArticle[]; size_buckets: SizeBucket[]; days: DayRow[]; orders: OrderRow[];
};

type Drawer =
    | { kind: 'warehouses'; title: string; rows: StockArticle['by_warehouse'] }
    | { kind: 'orders'; title: string; orderNumbers: string[] };

/**
 * Modul 40.31, Pregled skladista i narudzbi (task.md F7, zahtjev E; N10 u
 * TASK-40-61). Cetiri bloka, svaki sklopljen, sa zbrojem vidljivim i kad je
 * sklopljen; klik na redak otvara drawer s razradom. Izbor razine vrijedi za
 * sva cetiri bloka, prikaz po spotu ili po fineu takodjer.
 */
export function WarehouseOverviewModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [scopes, setScopes] = useState<Scopes | null>(null);
    const [level, setLevel] = useState<Level>('maloprodaja');
    const [value, setValue] = useState('');
    const [unit, setUnit] = useState<Unit>('spot');
    const [stock, setStock] = useState<StockResp | null>(null);
    const [orders, setOrders] = useState<OrdersResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const [drawer, setDrawer] = useState<Drawer | null>(null);

    useEffect(() => {
        api<Scopes>('/warehouse-overview/scopes', token).then(setScopes).catch((e) => {
            // Greska se prikazuje, ne guta se u prazan izbor (audit 40.x, 6. tablica).
            setError(e instanceof ApiError ? e.message : t('Učitavanje razina nije uspjelo.', 'Loading levels failed.'));
            setScopes({ warehouses: [], regions: [], countries: ['HR'] });
        });
    }, [token]);

    const options = useMemo(() => {
        if (!scopes) return [] as { v: string; l: string }[];
        if (level === 'poslovnica') return scopes.warehouses.map((w) => ({ v: w.code, l: `${w.code} · ${w.name}` }));
        if (level === 'regija') return scopes.regions.map((r) => ({ v: r, l: r }));
        if (level === 'drzava') return scopes.countries.map((c) => ({ v: c, l: c === 'HR' ? t('Hrvatska', 'Croatia') : c === 'BG' ? t('Bugarska', 'Bulgaria') : c }));
        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopes, level, locale]);

    useEffect(() => {
        if (level !== 'maloprodaja' && !options.some((o) => o.v === value)) setValue(options[0]?.v ?? '');
    }, [level, options, value]);

    useEffect(() => {
        if (level !== 'maloprodaja' && !value) return;
        const qs = new URLSearchParams({ level });
        if (level !== 'maloprodaja') qs.set('value', value);
        setLoading(true);
        setError(null);
        // Zakasnjeli odgovor za staru razinu se odbacuje (audit 40.x, 6. tablica).
        let stale = false;
        Promise.all([
            api<StockResp>(`/warehouse-overview/stock?${qs}`, token),
            api<OrdersResp>(`/warehouse-overview/orders?${qs}`, token),
        ])
            .then(([s, o]) => { if (!stale) { setStock(s); setOrders(o); } })
            .catch((e) => { if (!stale) setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')); })
            .finally(() => { if (!stale) setLoading(false); });
        return () => { stale = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level, value, token]);

    const orderByNumber = useMemo(() => new Map((orders?.orders ?? []).map((o) => [o.order_number, o])), [orders]);

    const u = (s: { spot_eur: number; fine_g: number }) => (unit === 'spot' ? formatMoney(s.spot_eur, locale) : `${formatNumber(s.fine_g, locale)} g`);
    const unitHead = unit === 'spot' ? t('Po spotu', 'At spot') : t('Fino', 'Fine');
    const toggle = (k: string) => setOpen((p) => ({ ...p, [k]: !p[k] }));
    const metalSum = (tot: Record<Metal, Sums>) => `AU ${u(tot.AU)} · AG ${u(tot.AG)}`;

    return (
        <section className="card admin-block">
            {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ marginBottom: 8 }}>← {t('Natrag na Skladišno', 'Back to Warehouse')}</button> : null}
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📋</div>
                <div className="bi-module-hero-text">
                    <h1>40.31 · {t('Pregled skladišta i narudžbi', 'Stock and orders overview')}</h1>
                    <p className="muted">{t('Zaliha poslovnica, neisporučene narudžbe po artiklu i veličini, otvorene narudžbe po danu nastanka.', 'Branch stock, undelivered orders by item and size, open orders by day of entry.')}</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <label>{t('Razina', 'Level')}<br />
                    <select value={level} onChange={(e) => setLevel(e.target.value as Level)}>
                        <option value="poslovnica">{t('Poslovnica', 'Branch')}</option>
                        <option value="regija">{t('Regija', 'Region')}</option>
                        <option value="maloprodaja">{t('Cijela maloprodaja', 'All retail')}</option>
                        <option value="drzava">{t('Država', 'Country')}</option>
                    </select>
                </label>
                {level !== 'maloprodaja' ? (
                    <label>{t('Odabir', 'Selection')}<br />
                        <select value={value} onChange={(e) => setValue(e.target.value)} style={{ minWidth: 220 }}>
                            {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                    </label>
                ) : null}
                <div>
                    <button type="button" className={unit === 'spot' ? 'compact' : 'secondary compact'} onClick={() => setUnit('spot')}>{t('Po spotu', 'At spot')}</button>{' '}
                    <button type="button" className={unit === 'fine' ? 'compact' : 'secondary compact'} onClick={() => setUnit('fine')}>{t('Po fineu', 'Fine grams')}</button>
                </div>
                {loading ? <span className="muted">{t('Učitavam…', 'Loading…')}</span> : null}
            </div>

            {stock ? (
                <p className="muted" style={{ marginTop: 0 }}>
                    {t('Spot', 'Spot')} AU {formatMoney(stock.spot.gold_per_gram, locale)}/g{stock.spot.spot_date ? ` (${formatDate(stock.spot.spot_date, locale)})` : ''}
                    {' · '}AG {formatMoney(stock.spot.silver_per_gram, locale)}/g {t('(procjena AU/85, nema srebrnog feeda)', '(estimate AU/85, no silver feed)')}
                    {stock.stock_as_of ? ` · ${t('zaliha na dan', 'stock as of')} ${formatDate(stock.stock_as_of, locale)}` : ''}
                </p>
            ) : null}
            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            {stock ? (
                <Block id="e1" title={`E1 · ${t('Skladišta', 'Stock')}`} summary={metalSum(stock.totals)} open={!!open.e1} onToggle={toggle}>
                    <SumLine locale={locale} t={t} u={u} totals={stock.totals} imamLabel={t('smješteno', 'placed')} freeLabel={t('slobodna prodaja', 'free for sale')} />
                    <table className="table">
                        <thead><tr><th>{t('Šifra', 'SKU')}</th><th>{t('Naziv', 'Name')}</th><th></th><th>{unitHead}</th><th>{t('Kom', 'Qty')}</th><th>{t('Smješteno', 'Placed')}</th><th>{t('Slobodna prodaja', 'Free for sale')}</th></tr></thead>
                        <tbody>
                            {stock.articles.map((a) => (
                                <tr key={a.sku} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ kind: 'warehouses', title: `${a.sku} · ${a.item_name}`, rows: a.by_warehouse })}>
                                    <td>{a.sku}</td><td>{a.item_name}</td><td>{a.metal}</td><td>{u(a)}</td>
                                    <td>{formatNumber(a.qty, locale)}</td><td>{formatNumber(a.imam_ju_qty, locale)}</td><td>{formatNumber(a.free_qty, locale)}</td>
                                </tr>
                            ))}
                            {stock.articles.length === 0 ? <tr><td colSpan={7} className="muted">{t('Nema zalihe za odabir.', 'No stock for the selection.')}</td></tr> : null}
                        </tbody>
                    </table>
                </Block>
            ) : null}

            {orders ? (
                <>
                    <Block id="e2" title={`E2 · ${t('Neisporučene narudžbe po artiklu', 'Undelivered orders by item')}`} summary={`${orders.totals.orders} ${t('narudžbi', 'orders')} · ${formatMoney(orders.totals.total_eur, locale)} · ${metalSum(orders.totals)}`} open={!!open.e2} onToggle={toggle}>
                        <SumLine locale={locale} t={t} u={u} totals={orders.totals} imamLabel={t('smješteno', 'placed')} freeLabel={t('za nabavu', 'to procure')} />
                        {orders.totals.header_minus_items_eur || orders.totals.packaging_eur ? (
                            <p className="muted">
                                {t('Iznos narudžbi', 'Order amount')} {formatMoney(orders.totals.total_eur, locale)} · {t('zbroj stavki', 'sum of items')} {formatMoney(orders.totals.items_eur ?? 0, locale)}{orders.totals.packaging_eur ? <> · {t('ambalaža', 'packaging')} {formatMoney(orders.totals.packaging_eur, locale)}</> : null} · {t('razlika zaglavlje - stavke', 'header minus items')} {formatMoney(orders.totals.header_minus_items_eur ?? 0, locale)}
                            </p>
                        ) : null}
                        <table className="table">
                            <thead><tr><th>{t('Artikl', 'Item')}</th><th></th><th>{unitHead}</th><th>EUR</th><th>{t('Kom', 'Qty')}</th><th>{t('Smješteno', 'Placed')}</th><th>{t('Za nabavu', 'To procure')}</th><th>{t('Narudžbi', 'Orders')}</th></tr></thead>
                            <tbody>
                                {orders.articles.map((a) => (
                                    <tr key={`${a.metal}|${a.item_name}`} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ kind: 'orders', title: a.item_name, orderNumbers: a.order_numbers })}>
                                        <td>{a.item_name}</td><td>{a.metal}</td><td>{u(a)}</td><td>{formatMoney(a.book_eur, locale)}</td>
                                        <td>{formatNumber(a.qty, locale)}</td><td>{formatNumber(a.imam_ju_qty, locale)}</td><td>{formatNumber(a.free_qty, locale)}</td><td>{a.order_count}</td>
                                    </tr>
                                ))}
                                {orders.articles.length === 0 ? <tr><td colSpan={8} className="muted">{t('Nema neisporučenih narudžbi za odabir.', 'No undelivered orders for the selection.')}</td></tr> : null}
                            </tbody>
                        </table>
                    </Block>

                    <Block id="e3" title={`E3 · ${t('Neisporučene narudžbe po veličini', 'Undelivered orders by size')}`} summary={`${orders.totals.orders} ${t('narudžbi', 'orders')} · ${formatMoney(orders.totals.total_eur, locale)}`} open={!!open.e3} onToggle={toggle}>
                        <table className="table">
                            <thead><tr><th>{t('Iznos narudžbe (EUR)', 'Order amount (EUR)')}</th><th>{t('Narudžbi', 'Orders')}</th><th>{t('Ukupno EUR', 'Total EUR')}</th><th>{unitHead}</th></tr></thead>
                            <tbody>
                                {orders.size_buckets.map((b) => (
                                    <tr key={b.key} style={{ cursor: b.orders ? 'pointer' : undefined }} onClick={() => b.orders && setDrawer({ kind: 'orders', title: `${t('Narudžbe', 'Orders')} ${b.key} EUR`, orderNumbers: b.order_numbers })}>
                                        <td>{b.key.replace('+', ' ' + t('i više', 'and above'))}</td><td>{b.orders}</td><td>{formatMoney(b.total_eur, locale)}</td>
                                        <td>{unit === 'spot' ? formatMoney(b.spot_eur, locale) : `AU ${formatNumber(b.au_fine_g, locale)} g · AG ${formatNumber(b.ag_fine_g, locale)} g`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Block>

                    <Block id="e4" title={`E4 · ${t('Otvorene narudžbe po danu nastanka', 'Open orders by day of entry')}`} summary={orders.days.length ? `${formatDate(orders.days[0].date ?? '', locale)} - ${formatDate(orders.days.filter((d) => d.date).slice(-1)[0]?.date ?? '', locale)} · ${orders.days.length} ${t('dana', 'days')}` : t('nema otvorenih narudžbi', 'no open orders')} open={!!open.e4} onToggle={toggle}>
                        <table className="table">
                            <thead><tr><th>{t('Datum', 'Date')}</th><th>{t('Narudžbi', 'Orders')}</th><th>AU {unit === 'spot' ? 'EUR' : 'g'}</th><th>AG {unit === 'spot' ? 'EUR' : 'g'}</th></tr></thead>
                            <tbody>
                                {orders.days.map((d) => (
                                    <tr key={d.date ?? 'bez-datuma'} style={{ cursor: d.orders ? 'pointer' : undefined, opacity: d.orders ? 1 : 0.5 }} onClick={() => d.orders && setDrawer({ kind: 'orders', title: d.date ? formatDate(d.date, locale) : t('bez datuma', 'no date'), orderNumbers: d.order_numbers })}>
                                        <td>{d.date ? formatDate(d.date, locale) : t('bez datuma', 'no date')}</td><td>{d.orders || ''}</td>
                                        <td>{d.orders ? (unit === 'spot' ? formatMoney(d.au_eur, locale) : formatNumber(d.au_fine_g, locale)) : ''}</td>
                                        <td>{d.orders ? (unit === 'spot' ? formatMoney(d.ag_eur, locale) : formatNumber(d.ag_fine_g, locale)) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Block>
                </>
            ) : null}

            {drawer ? (
                <SideDrawer title={drawer.title} onClose={() => setDrawer(null)}>
                    {drawer.kind === 'warehouses' ? (
                        <table className="table">
                            <thead><tr><th>{t('Skladište', 'Warehouse')}</th><th>{t('Kom', 'Qty')}</th><th>{t('Smješteno', 'Placed')}</th><th>{t('Slobodno', 'Free')}</th><th>{unitHead}</th></tr></thead>
                            <tbody>
                                {drawer.rows.map((w) => (
                                    <tr key={w.warehouse_code}>
                                        <td>{w.warehouse_code} · {w.warehouse_name}</td><td>{formatNumber(w.qty, locale)}</td>
                                        <td>{formatNumber(w.imam_ju_qty, locale)}</td><td>{formatNumber(w.free_qty, locale)}</td><td>{u(w)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="table">
                            <thead><tr><th>{t('Narudžba', 'Order')}</th><th>{t('Datum', 'Date')}</th><th>{t('Partner', 'Partner')}</th><th>EUR</th><th>AU g</th><th>AG g</th></tr></thead>
                            <tbody>
                                {drawer.orderNumbers.map((n) => orderByNumber.get(n)).filter((o): o is OrderRow => !!o).map((o) => (
                                    <tr key={o.order_number}>
                                        <td>{o.order_number}{o.imam_ju ? <span className="muted"> · {t('smješteno', 'placed')}</span> : null}</td>
                                        <td>{o.date ? formatDate(o.date, locale) : ''}</td>
                                        <td>{o.partner_name}</td>
                                        <td>{formatMoney(o.total_eur, locale)}</td>
                                        <td>{formatNumber(o.au_fine_g, locale)}</td>
                                        <td>{formatNumber(o.ag_fine_g, locale)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </SideDrawer>
            ) : null}
        </section>
    );
}

function Block({ id, title, summary, open, onToggle, children }: { id: string; title: string; summary: string; open: boolean; onToggle: (id: string) => void; children: React.ReactNode }) {
    return (
        <div className="card" style={{ padding: '0.8rem 1rem', marginBottom: '0.8rem' }}>
            <button type="button" onClick={() => onToggle(id)} style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'baseline', width: '100%' }}>
                <span style={{ width: 14 }}>{open ? '▾' : '▸'}</span>
                <strong>{title}</strong>
                <span className="muted" style={{ marginLeft: 'auto' }}>{summary}</span>
            </button>
            {open ? <div style={{ marginTop: '0.6rem', overflowX: 'auto' }}>{children}</div> : null}
        </div>
    );
}

function SumLine({ locale, t, u, totals, imamLabel, freeLabel }: {
    locale: Locale; t: (hr: string, en: string) => string; u: (s: { spot_eur: number; fine_g: number }) => string;
    totals: Record<Metal, Sums>; imamLabel: string; freeLabel: string;
}) {
    return (
        <table className="table" style={{ marginBottom: '0.6rem' }}>
            <thead><tr><th>{t('Zbroj', 'Total')}</th><th>{t('Vrijednost', 'Value')}</th><th>{t('Kom', 'Qty')}</th><th>{imamLabel}</th><th>{freeLabel}</th></tr></thead>
            <tbody>
                {(['AU', 'AG'] as Metal[]).map((m) => (
                    <tr key={m} style={{ fontWeight: 600 }}>
                        <td>{m}</td><td>{u(totals[m])}</td><td>{formatNumber(totals[m].qty, locale)}</td>
                        <td>{formatNumber(totals[m].imam_ju_qty, locale)}</td><td>{formatNumber(totals[m].free_qty, locale)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function SideDrawer({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1000 }}>
            <aside onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(640px, 100%)', background: '#fff', padding: '1rem 1.1rem', overflowY: 'auto', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
                    <button type="button" onClick={onClose} style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 6, cursor: 'pointer' }}>×</button>
                </div>
                {children}
            </aside>
        </div>
    );
}
